import { useId } from 'react';
import { Box, type BoxProps } from '@mantine/core';
import { exploreHeroBagelSvgMarkup } from './exploreHeroBagelSvgString';

const BIG_STAR_PATH =
  'M19.5371 2.12891C19.8018 1.18645 21.1042 1.1573 21.4336 2.04102L21.4629 2.12891L24.8232 14.0996C25.0885 15.044 25.7972 15.7951 26.7148 16.1182L26.9004 16.1768L38.8711 19.5371C39.8135 19.8018 39.8427 21.1042 38.959 21.4336L38.8711 21.4629L26.9004 24.8232C25.956 25.0885 25.2049 25.7972 24.8818 26.7148L24.8232 26.9004L21.4629 38.8711C21.1982 39.8135 19.8958 39.8427 19.5664 38.959L19.5371 38.8711L16.1768 26.9004C15.9115 25.956 15.2028 25.2049 14.2852 24.8818L14.0996 24.8232L2.12891 21.4629C1.18645 21.1982 1.1573 19.8958 2.04102 19.5664L2.12891 19.5371L14.0996 16.1768C15.044 15.9115 15.7951 15.2028 16.1182 14.2852L16.1768 14.0996L19.5371 2.12891Z';

const SMALL_STAR_PATH =
  'M9.03711 2.12891C9.30182 1.18645 10.6042 1.15731 10.9336 2.04102L10.9629 2.12891L12.0215 5.90039C12.2867 6.84492 12.9953 7.59677 13.9131 7.91992L14.0996 7.97852L17.8711 9.03711C18.8135 9.30183 18.8427 10.6042 17.959 10.9336L17.8711 10.9629L14.0996 12.0215C13.1551 12.2867 12.4032 12.9953 12.0801 13.9131L12.0215 14.0996L10.9629 17.8711C10.6982 18.8135 9.3958 18.8427 9.06641 17.959L9.03711 17.8711L7.97852 14.0996C7.71335 13.1551 7.00465 12.4032 6.08691 12.0801L5.90039 12.0215L2.12891 10.9629C1.18645 10.6982 1.15731 9.3958 2.04102 9.06641L2.12891 9.03711L5.90039 7.97852C6.84492 7.71335 7.59677 7.00465 7.91992 6.08691L7.97852 5.90039L9.03711 2.12891Z';

type Variant = 'hero' | 'insights';

export type ExploreBagelSparkleIconProps = BoxProps & {
  variant?: Variant;
  showSparkles?: boolean;
};

/** Compact vector bagel for insights chip (not the hero asset). */
function InsightsBagelGraphic({ filterId, clipId }: { filterId: string; clipId: string }) {
  return (
    <Box
      component="svg"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 200 200"
      fill="none"
      aria-hidden
      sx={{
        position: 'absolute',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        width: 20,
        height: 20,
        zIndex: 0,
        pointerEvents: 'none',
      }}
    >
      <defs>
        <filter
          id={filterId}
          x="0"
          y="0.236946"
          width="199.763"
          height="199.763"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dx="13.0157" dy="1.1318" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0.309739 0 0 0 0 0.154913 0 0 0 0 0.051695 0 0 0 0.08 0"
          />
          <feBlend mode="multiply" in2="shape" result="effect1_innerShadow_1441_31496" />
        </filter>
        <clipPath id={clipId}>
          <rect width="200" height="200" fill="white" />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        <g filter={`url(#${filterId})`}>
          <path
            d="M99.8818 0.236946C155.045 0.237111 199.763 44.9558 199.763 100.119C199.763 155.282 155.045 199.999 99.8818 200C44.7189 200 0.000164952 155.282 0 100.119C0 44.9557 44.7188 0.236946 99.8818 0.236946ZM99.8818 60.9547C78.2523 60.9547 60.7178 78.4892 60.7178 100.119C60.7179 121.748 78.2524 139.282 99.8818 139.282C121.511 139.282 139.045 121.748 139.045 100.119C139.045 78.4893 121.511 60.9549 99.8818 60.9547Z"
            fill="#FF8701"
          />
        </g>
        <path
          opacity="0.3"
          d="M155.797 23.2871C164.386 29.7325 171.621 37.8068 177.09 47.0484C182.558 56.2901 186.154 66.5185 187.67 77.1494C189.185 87.7805 188.592 98.6065 185.924 109.008C183.256 119.41 178.565 129.184 172.12 137.773L171.893 137.603C176.381 130.454 179.744 122.642 181.848 114.439C184.515 104.038 185.109 93.2121 183.593 82.5811C182.078 71.9501 178.482 61.7219 173.014 52.4801C167.545 43.2384 160.31 35.1642 151.721 28.7187C143.132 22.2733 133.357 17.5826 122.955 14.9147C112.554 12.2467 101.728 11.653 91.0971 13.1687C80.4661 14.6845 70.2378 18.2798 60.9961 23.7485C53.7082 28.061 47.1477 33.4731 41.5381 39.7804L41.3108 39.6098C47.7563 31.0208 55.8305 23.7854 65.0722 18.3168C74.3139 12.8482 84.5422 9.25275 95.1732 7.73705C105.804 6.22134 116.63 6.81504 127.032 9.48299C137.433 12.151 147.208 16.8416 155.797 23.2871Z"
          fill="white"
        />
      </g>
    </Box>
  );
}

export function ExploreBagelSparkleIcon({
  sx,
  variant = 'hero',
  showSparkles = true,
  ...props
}: ExploreBagelSparkleIconProps) {
  const rid = useId().replace(/:/g, '');
  const filterId = `${rid}-bagel-filter`;
  const clipId = `${rid}-bagel-clip`;

  if (variant === 'insights') {
    return (
      <Box
        component="span"
        role="img"
        aria-label="Bagel"
        sx={{
          position: 'relative',
          display: 'inline-block',
          width: 28,
          height: 28,
          flexShrink: 0,
          overflow: 'visible',
          ...sx,
        }}
        {...props}
      >
        <InsightsBagelGraphic filterId={filterId} clipId={clipId} />
        {showSparkles ? (
          <Box
            component="svg"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 41 41"
            fill="none"
            aria-hidden
            sx={{
              position: 'absolute',
              right: 0,
              top: 0,
              width: 14,
              height: 14,
              zIndex: 1,
              pointerEvents: 'none',
              overflow: 'visible',
            }}
          >
            <path
              d={BIG_STAR_PATH}
              fill="none"
              stroke="#FFFFFF"
              strokeWidth={2.75}
              strokeLinejoin="round"
            />
            <path
              d={BIG_STAR_PATH}
              fill="#9F9AF9"
              stroke="#09132D"
              strokeWidth={1.1}
              strokeLinejoin="round"
            />
          </Box>
        ) : null}
      </Box>
    );
  }

  return (
    <Box
      component="span"
      role="img"
      aria-label="Bagel"
      sx={{
        position: 'relative',
        display: 'inline-block',
        width: 64,
        height: 64,
        flexShrink: 0,
        overflow: 'visible',
        ...sx,
      }}
      {...props}
    >
      {!showSparkles ? (
        <Box
          component="span"
          aria-hidden
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            boxSizing: 'border-box',
            lineHeight: 0,
            pointerEvents: 'none',
            // Hero SVG embeds a raster: forcing both width+height to 100% clips the image.
            // Let the SVG size to its aspect ratio within the box (meet), same as preserveAspectRatio.
            '& svg': {
              display: 'block',
              width: 'auto',
              height: 'auto',
              maxWidth: '100%',
              maxHeight: '100%',
              margin: 'auto',
              flexShrink: 0,
              overflow: 'visible',
            },
          }}
          dangerouslySetInnerHTML={{ __html: exploreHeroBagelSvgMarkup }}
        />
      ) : (
        <Box
          component="span"
          aria-hidden
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: 54,
            height: 54,
            zIndex: 0,
            pointerEvents: 'none',
            lineHeight: 0,
            '& svg': {
              width: '100%',
              height: '100%',
              display: 'block',
              objectFit: 'contain',
            },
          }}
          dangerouslySetInnerHTML={{ __html: exploreHeroBagelSvgMarkup }}
        />
      )}
      {showSparkles ? (
        <>
          <Box
            component="svg"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 41 41"
            fill="none"
            aria-hidden
            sx={{
              position: 'absolute',
              left: -5,
              top: -10,
              width: 15,
              height: 15,
              zIndex: 1,
              pointerEvents: 'none',
            }}
          >
            <path
              d={BIG_STAR_PATH}
              fill="#9F9AF9"
              stroke="#09132D"
              strokeWidth={2}
            />
          </Box>

          <Box
            component="svg"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden
            sx={{
              position: 'absolute',
              left: 10,
              top: 4,
              width: 6,
              height: 6,
              zIndex: 2,
              pointerEvents: 'none',
            }}
          >
            <path
              d={SMALL_STAR_PATH}
              fill="#9F9AF9"
              stroke="#09132D"
              strokeWidth={2}
            />
          </Box>
        </>
      ) : null}
    </Box>
  );
}
